import { observable, computed, action, asStructure, autorun } from 'mobx'

import { browserHistory } from 'react-router'

import $ from 'jquery'

import {
  windowDimensions,
  windowScroll,
  throttle,
  closePopout
} from 'utils'

import { menu, siteTitleShorter, projectTypes, defaultMetaDescription } from 'config'

class UiStore {
  // --------------------------------------------------------------------------
  // Window facts
  // --------------------------------------------------------------------------

  @observable windowDimensions = asStructure(windowDimensions())

  @computed get windowWidth () {
    return this.windowDimensions.width
  }

  @observable windowScroll = asStructure(windowScroll())

  @computed get scrollY () {
    return this.windowScroll.y
  }

  @computed get scrollLock () {
    // currently, only need to lock the window scroll when the
    // popout is open. in the future, can just add additional predicates with ||
    // see bottom for autorun effect
    return this.showPopout
  }

  // --------------------------------------------------------------------------
  // Major UI Facts
  // --------------------------------------------------------------------------

  // Routing

  @observable route = null

  @observable historyObject = null

  @computed get pathname () {
    return this.historyObject ? this.historyObject.pathname : null
  }

  @computed get showLanding () {
    return this.route === 'landing'
  }

  @computed get pageItem () {
    // get full menu item config for route slug
    return menu.filter(({ slug }) => slug === this.route)[0]
  }

  @computed get pageTitle () {
    return this.pageItem && this.pageItem.name
  }

  rootRouteTransition (name) {
    this.route = name
  }

  routeTransition (e) {
    this.historyObject = e
  }

  // Landing mounted?

  @observable landingMounted = false

  @action landingDidMount () {
    this.landingMounted = true
  }

  // Document subtitle parts (after the page name)
  @computed get documentSubtitle () {
    return this.showPopout ? [ this.popoutElement.name ] : []
  }

  @computed get documentTitle () {
    const item = this.pageItem

    // make it easier to spread
    const pageTitleButter = item ? [ item.name ] : []

    const secondary = [
      ...pageTitleButter,
      ...this.documentSubtitle
    ].join(' – ')

    return secondary + (secondary.length === 0 ? '' : ' | ') + siteTitleShorter
  }

  @computed get documentMetaDescription () {
    if (this.pathname in window.portalMetaDescriptions) {
      return window.portalMetaDescriptions[this.pathname]
    } else if (this.showPopout && this.popoutElement.descMeta) {
      return this.popoutElement.descMeta
    } else if (this.showPopout) {
      // May be temporary for testing, default to first 160 characters of content
      const htmlStripped = this.popoutElement.content.replace(/<(?:.|\n)*?>/gm, '')
      return /(.{0,160})\s/.exec(htmlStripped)[1]
    }
    return defaultMetaDescription
  }

  // Show menu

  @observable showMenu = false

  @action toggleMenu () {
    this.showMenu = !this.showMenu
    this.resetExpandedMenuItems()
  }

  @action closeMenu () {
    this.showMenu = false
  }

  // Expanded Menu Items

  @observable expandedMenuItems = asStructure([])

  isExpandedMenuItem (itemSlug) {
    return this.expandedMenuItems.includes(itemSlug)
  }

  @action toggleMenuItemExpanded (itemSlug) {
    if (this.isExpandedMenuItem(itemSlug)) {
      this.expandedMenuItems = this.expandedMenuItems.filter(s => s !== itemSlug)
    } else {
      this.expandedMenuItems.push(itemSlug)
    }
  }

  @action resetExpandedMenuItems () {
    this.expandedMenuItems = []
  }

  // Show advanced filters

  @observable showAdvancedFilters = true

  @action toggleAdvancedFilters () {
    this.showAdvancedFilters = !this.showAdvancedFilters
  }

  // Popout element (project, resource, story, event, etc)

  @observable popoutElement = null

  @computed get showPopout () {
    return !!this.popoutElement
  }

  // Id of element being hovered (project, resource, etc)

  @observable hoverId = null

  // Pagination state

  get defaultPagination () {
    return {
      current: 1,
      last: -1
    }
  }

  @observable pagination = asStructure(this.defaultPagination)

  @action resetPagination () {
    this.pagination = this.defaultPagination
  }

  @action setCurrentPage (page) {
    this.pagination.current = page
  }

  // Landing scroll to panel next (should be nulled once received)
  @observable scrollToLandingPanelNext = null

  // Popout image lightbox visibility
  @observable lightboxIsVisible = false

  // About page
  @observable hoveredTag = null
  @observable hoveredContact = null

  @observable selectedTag = null
  @observable selectedContact = null

  @computed get inAboutDetailView () {
    return this.selectedTag || this.selectedContact
  }

  @computed get inAboutSingleDetailView () {
    return this.selectedContact !== null
  }

  @action resetSelectedTagAndContact () {
    this.selectedContact = null
    this.selectedTag = null
  }

  // --------------------------------------------------------------------------
  // Requests
  // --------------------------------------------------------------------------

  @observable requests = asStructure([])

  @computed get showProgress () {
    return this.requests.length !== 0
  }

  @action trackRequest (ns, promise) {
    this.requests.push({ ns, promise })

    const remove = () => {
      this.removeRequest(promise)
    }

    promise.then(remove).catch(remove)
  }

  @action removeRequest (promise) {
    this.requests = this.requests.filter(a => a.promise !== promise)
  }

  // --------------------------------------------------------------------------
  // Filters
  // --------------------------------------------------------------------------

  // Project filters

  get defaultProjectFilters () {
    return {
      sort: 'alpha-asc',
      search: '',
      faculty: '',
      tag: ''
    }
  }

  @observable projectFilters = asStructure(this.defaultProjectFilters)

  @observable projectType = ''

  @action changeProjectType (newType) {
    this.projectType = newType
  }

  // Map projectType into a json object that could be used to get data from backend
  @computed get activeProjectTypes () {
    return projectTypes.reduce((acc, f) => (
      f === this.projectType ? { [f]: true, ...acc } : { [f]: false, ...acc }
    ), {})
  }

  @computed get projectFiltersWithType () {
    return { 'types': this.activeProjectTypes, ...this.projectFilters }
  }

  @action resetProjectFilters () {
    this.projectFilters = this.defaultProjectFilters
  }

  @action resetAdvancedProjectFilters () {
    const defaultFilters = this.defaultProjectFilters
    this.projectFilters.faculty = defaultFilters.faculty
    this.projectFilters.tag = defaultFilters.tag
  }

  updateProjectFiltersInUrlBar () {
    const changedParams = Object.keys(this.projectFilters).reduce((acc, f) => (
      this.projectFilters[f] !== this.defaultProjectFilters[f]
        ? { [f]: this.projectFilters[f], ...acc } : acc
    ), {})

    browserHistory.replace({
      pathname: this.pathname,
      query: changedParams
    })

    this.sendPageViewToAnalytics()
  }

  @action updateProjectFiltersInUiStoreFromUrl (query) {
    for (const filter of Object.keys(this.projectFilters)) {
      if (query[filter]) {
        this.projectFilters[filter] = query[filter]
      }
    }
  }

  // Resource filters

  get defaultResourceFilters () {
    return {
      search: ''
    }
  }

  @observable resourceFilters = asStructure(this.defaultResourceFilters)

  @action resetResourceFilters () {
    this.resourceFilters = this.defaultResourceFilters
  }

  updateResourceFiltersInUrlBar () {
    const changedParams = Object.keys(this.resourceFilters).reduce((acc, f) => (
      this.resourceFilters[f] !== this.defaultResourceFilters[f]
        ? { [f]: this.resourceFilters[f], ...acc } : acc
    ), {})

    browserHistory.replace({
      pathname: this.pathname,
      query: changedParams
    })

    this.sendPageViewToAnalytics()
  }

  @action updateResourceFiltersInUiStoreFromUrl (query) {
    const notAContentCategory = a => a !== 'category' && a !== 'subcategory'

    for (const filter of Object.keys(this.projectFilters)) {
      if (query[filter] && notAContentCategory(filter)) {
        this.resourceFilters[filter] = query[filter]
      }
    }
  }

  // Site-wide search filters
  get defaultSitewideSearchFilters () {
    return {
      search: ''
    }
  }

  @observable sitewideSearchFilters = asStructure(this.defaultSitewideSearchFilters)

  @action resetSitewideSearchFilters () {
    this.sitewideSearchFitlers = this.defaultSitewideSearchFilters
  }

  updateSitewideSearchFiltersInUrl () {
    const search = this.sitewideSearchFilters.search || undefined

    browserHistory.replace({
      pathname: this.pathname,
      query: { search }
    })
  }

  @observable sitewideSearchContentType = 'nih/nsf grants'

  @observable showSearchContentTypeSelector = false

  // Story filters

  get defaultStoryFilters () {
    return {
      tags: []
    }
  }

  @observable storyFilters = asStructure(this.defaultStoryFilters)

  @action resetStoryFilters () {
    this.storyFilters = this.defaultStoryFilters
  }

  // Expertise filters

  get defaultExpertiseFilters () {
    return {
      search: ''
    }
  }

  @observable expertiseFilters = asStructure(this.defaultExpertiseFilters)

  @action resetExpertiseFilters () {
    this.expertiseFilters = this.defaultExpertiseFilters
  }

  sendPageViewToAnalytics () {
    // The autotrack plugin sends pageviews to Google whenever we "push" a new
    // page to the history stack. This function is used when we "replace".
    const page = `${this.pathname}${this.historyObject.search}`
    window.ga('set', 'page', page)
    window.ga('send', 'pageview')
  }

  // --------------------------------------------------------------------------
  // Constructor
  // --------------------------------------------------------------------------

  constructor () {
    window.addEventListener('resize', throttle(() => {
      this.windowDimensions = windowDimensions()
    }, 30))

    window.addEventListener('scroll', () => {
      this.windowScroll = windowScroll()
    })

    document.addEventListener('keydown', e => {
      const code = e.keyCode || e.which

      // check if it's the escape key
      if (code === 27) {
        if (this.showPopout) {
          closePopout()
        } else if (this.showMenu) {
          this.closeMenu()
        }
      }
    })

    //
    // Internal effects (probably should be @computed instead)
    //

    autorun(() => {
      if (this.showLanding || this.showPopout) {
        // don't show menu  if showing the landing or popout
        this.closeMenu()
      }
    })

    //
    // External effects
    //

    autorun(() => {
      document.title = this.documentTitle
    })

    autorun(() => {
      $('meta[name=description]').attr('content', this.documentMetaDescription)
    })

    autorun(() => {
      document.body.className = this.scrollLock ? 'locked' : ''
    })

    //
    // Kind of an internal effect. We want to reset selections when the group
    // has changed. It's possible that there's a better way to invalidate
    // observables like this in MobX.
    //
  }
}

const singleton = new UiStore()
export default singleton



// WEBPACK FOOTER //
// ./resources/assets/js/stores/UiStore.js